import { CKEditor } from "@ckeditor/ckeditor5-react";
import { useEffect, useRef, useState } from "react";

import {
	AccessibilityHelp,
	Alignment,
	AutoImage,
	AutoLink,
	Autoformat,
	Autosave,
	Bold,
	ClassicEditor,
	Code,
	CodeBlock,
	Editor,
	Essentials,
	FileLoader,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	HtmlComment,
	HtmlEmbed,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	SelectAll,
	ShowBlocks,
	SimpleUploadAdapter,
	SourceEditing,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo,
	UploadAdapter,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

import axios from "axios";
import Cookies from "js-cookie";
import "./contenteditor.css";
import { changeApiVersion } from "../../utils";

export default function CKEditorBuild({
	value,
	onEventChangeHandler,
}: {
	value?: string;
	onEventChangeHandler: (value: string, editor?: any) => void;
}) {
	const editorContainerRef = useRef(null);
	const editorRef = useRef(null);
	const [isLayoutReady, setIsLayoutReady] = useState(false);
	const [sourceEditing, setSourceEditing] = useState(false);

	useEffect(() => {
		setIsLayoutReady(true);

		return () => setIsLayoutReady(false);
	}, []);

	const uploadAdapter = (loader: FileLoader): UploadAdapter => {
		return {
			upload: () => {
				return new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;

						if (file) {
							const formData = new FormData();

							formData.append("file", file);

							console.log({
								formData1: Object.fromEntries(formData),
							});

							axios
								.post(
									`${changeApiVersion(
										process.env.REACT_APP_MEDIA_URL,
										"2"
									)}`,
									formData,
									{
										headers: {
											"Content-Type":
												"multipart/form-data",
											Authorization: `Bearer ${Cookies.get(
												"bitcommerz-sass-admin-token"
											)}`,
										},
									}
								)
								.then(({ data }) => {
									console.log("data =>", data);
									resolve({
										default: `${process.env.REACT_APP_IMAGE_URL}${data.path}`,
									});
								});
						}
					} catch (error) {
						reject("Hello");
					}
				});
			},
			abort: () => {},
		};
	};

	function uploadPlugin(editor: Editor) {
		editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
			return uploadAdapter(loader);
		};
	}

	const editorConfig = {
		toolbar: {
			items: [
				"undo",
				"redo",
				"|",
				"sourceEditing",
				"showBlocks",
				"|",
				"heading",
				"style",
				"|",
				"fontSize",
				"fontFamily",
				"fontColor",
				"fontBackgroundColor",
				"|",
				"bold",
				"italic",
				"underline",
				"|",
				"link",
				"insertImage",
				"insertTable",
				"codeBlock",
				"|",
				"alignment",
				"|",
				"bulletedList",
				"numberedList",
				"todoList",
				"indent",
				"outdent",
			],
			shouldNotGroupWhenFull: true,
		},
		plugins: [
			AccessibilityHelp,
			Alignment,
			Autoformat,
			AutoImage,
			AutoLink,
			Autosave,
			Bold,
			Code,
			CodeBlock,
			Essentials,
			FontBackgroundColor,
			FontColor,
			FontFamily,
			FontSize,
			GeneralHtmlSupport,
			Heading,
			HorizontalLine,
			HtmlComment,
			HtmlEmbed,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Paragraph,
			PasteFromOffice,
			SelectAll,
			ShowBlocks,
			SimpleUploadAdapter,
			SourceEditing,
			Strikethrough,
			Style,
			Subscript,
			Superscript,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			TodoList,
			Underline,
			Undo,
		],
		extraPlugins: [uploadPlugin],
		fontFamily: {
			supportAllValues: true,
		},
		fontSize: {
			options: [10, 12, 14, "default", 18, 20, 22],
			supportAllValues: true,
		},
		heading: {
			options: [
				{
					model: "paragraph",
					title: "Paragraph",
					class: "ck-heading_paragraph",
				},
				{
					model: "heading1",
					view: "h1",
					title: "Heading 1",
					class: "ck-heading_heading1",
				},
				{
					model: "heading2",
					view: "h2",
					title: "Heading 2",
					class: "ck-heading_heading2",
				},
				{
					model: "heading3",
					view: "h3",
					title: "Heading 3",
					class: "ck-heading_heading3",
				},
				{
					model: "heading4",
					view: "h4",
					title: "Heading 4",
					class: "ck-heading_heading4",
				},
				{
					model: "heading5",
					view: "h5",
					title: "Heading 5",
					class: "ck-heading_heading5",
				},
				{
					model: "heading6",
					view: "h6",
					title: "Heading 6",
					class: "ck-heading_heading6",
				},
			],
		},
		htmlSupport: {
			allow: [
				{
					name: /^.*$/,
					styles: true,
					attributes: true,
					classes: true,
				},
			],
		},
		image: {
			toolbar: [
				"toggleImageCaption",
				"imageTextAlternative",
				"|",
				"imageStyle:inline",
				"imageStyle:wrapText",
				"imageStyle:breakText",
				"|",
				"resizeImage",
			],
		},
		// initialData:
		// 	'<h2>Congratulations on setting up CKEditor 5! 🎉</h2>\n<p>\n    You\'ve successfully created a CKEditor 5 project. This powerful text editor will enhance your application, enabling rich text editing\n    capabilities that are customizable and easy to use.\n</p>\n<h3>What\'s next?</h3>\n<ol>\n    <li>\n        <strong>Integrate into your app</strong>: time to bring the editing into your application. Take the code you created and add to your\n        application.\n    </li>\n    <li>\n        <strong>Explore features:</strong> Experiment with different plugins and toolbar options to discover what works best for your needs.\n    </li>\n    <li>\n        <strong>Customize your editor:</strong> Tailor the editor\'s configuration to match your application\'s style and requirements. Or even\n        write your plugin!\n    </li>\n</ol>\n<p>\n    Keep experimenting, and don\'t hesitate to push the boundaries of what you can achieve with CKEditor 5. Your feedback is invaluable to us\n    as we strive to improve and evolve. Happy editing!\n</p>\n<h3>Helpful resources</h3>\n<ul>\n    <li>📝 <a href="https://orders.ckeditor.com/trial/premium-features">Trial sign up</a>,</li>\n    <li>📕 <a href="https://ckeditor.com/docs/ckeditor5/latest/installation/index.html">Documentation</a>,</li>\n    <li>⭐️ <a href="https://github.com/ckeditor/ckeditor5">GitHub</a> (star us if you can!),</li>\n    <li>🏠 <a href="https://ckeditor.com">CKEditor Homepage</a>,</li>\n    <li>🧑‍💻 <a href="https://ckeditor.com/ckeditor-5/demo/">CKEditor 5 Demos</a>,</li>\n</ul>\n<h3>Need help?</h3>\n<p>\n    See this text, but the editor is not starting up? Check the browser\'s console for clues and guidance. It may be related to an incorrect\n    license key if you use premium features or another feature-related requirement. If you cannot make it work, file a GitHub issue, and we\n    will help as soon as possible!\n</p>\n',
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: "https://",
			decorators: {
				toggleDownloadable: {
					mode: "manual",
					label: "Downloadable",
					attributes: {
						download: "file",
					},
				},
				isExternal: {
					mode: "manual",
					label: "Open in a new tab",
					attributes: {
						target: "_blank",
					},
				},
				isExtendedWithNoFollow: {
					mode: "manual",
					label: "Nofollow",
					attributes: {
						rel: "noopener noreferrer nofollow",
					},
				},
			},
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true,
			},
		},
		menuBar: {
			isVisible: true,
		},
		placeholder: "Type or paste your content here!",
		style: {
			definitions: [
				{
					name: "Article category",
					element: "h3",
					classes: ["category"],
				},
				{
					name: "Title",
					element: "h2",
					classes: ["document-title"],
				},
				{
					name: "Subtitle",
					element: "h3",
					classes: ["document-subtitle"],
				},
				{
					name: "Info box",
					element: "p",
					classes: ["info-box"],
				},
				{
					name: "Side quote",
					element: "blockquote",
					classes: ["side-quote"],
				},
				{
					name: "Marker",
					element: "span",
					classes: ["marker"],
				},
				{
					name: "Spoiler",
					element: "span",
					classes: ["spoiler"],
				},
				{
					name: "Code (dark)",
					element: "pre",
					classes: ["fancy-code", "fancy-code-dark"],
				},
				{
					name: "Code (bright)",
					element: "pre",
					classes: ["fancy-code", "fancy-code-bright"],
				},
			],
		},
		table: {
			contentToolbar: [
				"tableColumn",
				"tableRow",
				"mergeTableCells",
				"tableProperties",
				"tableCellProperties",
			],
		},
	};

	const handleSourceEditingToggle = (editor: Editor) => {
		const sourceEditing = editor.plugins.get("SourceEditing");

		// Listen for the change in the 'isSourceEditing' state
		sourceEditing.on(
			"change:isSourceEditingMode",
			(evt, propertyName, newValue) => {
				if (newValue) {
					setSourceEditing(true);
				} else {
					setSourceEditing(false);
				}
			}
		);
	};

	return (
		<div className="main-container">
			<div
				className="editor-container editor-container_classic-editor editor-container_include-style"
				ref={editorContainerRef}
			>
				<div className="editor-container__editor">
					<div ref={editorRef}>
						{isLayoutReady && (
							<CKEditor
								editor={ClassicEditor as any}
								config={editorConfig as any}
								data={value}
								onReady={(editor) => {
									handleSourceEditingToggle(editor);
								}}
								onChange={(event, editor) => {
									const data = editor?.getData();
									onEventChangeHandler(data, editor);
								}}
							/>
						)}

						{/* show warning when source editor panel open */}
						{sourceEditing && (
							<div className="text-red-500 text-sm">
								<span>
									Warning: Please close Source Editor panel
									before saving.
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
